import { AfterContentInit, Directive, ElementRef, Input, OnDestroy, ContentChild, NgModule } from "@angular/core";
import { coerceBooleanProperty, BooleanInput } from "@vattenfall/util";

import { InputLabelDirective, InputsModule } from "./inputs";
import { ListsElementAccessor } from "./lists-accessor";

/**
 * The input sublabel text element
 */
@Directive({
  selector: "ui-input-sublabel",
})
export class InputSublabelDirective {
  constructor(public elementRef: ElementRef<HTMLElement>) {}
}

/**
 * The input option element
 */
@Directive({
  selector: "ui-input-option",
})
export class InputOptionDirective implements OnDestroy, AfterContentInit {
  /**
   * Value of the control.
   */
  @Input() value!: unknown;
  /**
   * Whether the control should be selected by default.
   */
  @Input()
  get selected(): boolean {
    return this._selected;
  }
  set selected(value: BooleanInput) {
    this._selected = coerceBooleanProperty(value);
    // We need this to tell the parent when the selected value change programmatically
    this.parentMarkForCheck();
  }
  /**
   * Label for input option element. Behaves the same as `<ui-input-label>` element.
   */
  @Input() label: string | null = null;
  /**
   * Sublabel for input option element. Behaves the same as `<ui-input-sublabel>` element.
   */
  @Input() sublabel: string | null = null;

  listsElementAccessor?: ListsElementAccessor;

  private _selected = false;

  private _hostMutationObserver: MutationObserver;

  constructor(public elementRef: ElementRef<HTMLElement>) {
    // This ensure that change detection is fired when the content of the label change
    this._hostMutationObserver = new MutationObserver(this.parentMarkForCheck);
    this._hostMutationObserver.observe(this.elementRef.nativeElement, {
      attributes: true,
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  ngOnDestroy() {
    this._hostMutationObserver.disconnect();
  }

  ngAfterContentInit() {
    if (this.inputLabelDirective) this.label = this.inputLabelDirective?.elementRef.nativeElement.textContent;
    if (this.inputSublabelDirective) this.sublabel = this.inputSublabelDirective?.elementRef.nativeElement.textContent;
    if (!this.label) this.label = this.getTextContent();
  }

  @ContentChild(InputLabelDirective) protected inputLabelDirective?: InputLabelDirective;
  @ContentChild(InputSublabelDirective) protected inputSublabelDirective?: InputSublabelDirective;

  private parentMarkForCheck = () => {
    if (this.listsElementAccessor) this.listsElementAccessor.changeDetectorRef.markForCheck();
  };

  private getTextContent() {
    return (
      Array.from(this.elementRef.nativeElement.childNodes).find((node) => node.nodeType === Node.TEXT_NODE)?.textContent ?? ""
    ).trim();
  }
}

/**
 * The `ListsModule` contains utilities for input components that selects multiple options.
 */
@NgModule({
  declarations: [InputSublabelDirective, InputOptionDirective],
  imports: [InputsModule],
  exports: [InputSublabelDirective, InputOptionDirective],
})
export class ListsModule {}
